import React, { Component } from 'react';
import { Dialog } from 'primereact/dialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { Skeleton } from 'primereact/skeleton';
import moment from 'moment';
import ImageSlider from '../ImageSlider';
import '../MapboxMap/EmpreendimentoModal.css';

moment.locale('pt-br');

const currency = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
const number = new Intl.NumberFormat('pt-BR', { style: 'decimal', maximumFractionDigits: 2 });

const TIPO_NEGOCIO_LABELS = { Venda: 'Revenda', venda: 'Revenda', Aluguel: 'Locação', aluguel: 'Locação' };

const Item = ({ label, value, full }) => (
    <div className={'emp-item' + (full ? ' emp-item--full' : '')}>
        <span className="emp-item__label">{label}</span>
        <span className="emp-item__value">
            {value === null || value === undefined || value === '' ? '—' : value}
        </span>
    </div>
);

const Section = ({ title, icon, grid = true, children }) => (
    <div className="emp-section">
        <h6 className="emp-section__title">{icon && <i className={icon} />}{title}</h6>
        {grid ? <div className="emp-grid">{children}</div> : children}
    </div>
);

/**
 * Modal de detalhe de um imóvel (Revenda e Locação, Mapbox). Recebe o objeto do
 * imóvel já carregado (via crawler getEmpreendimento) e o exibe com o mesmo
 * layout do modal de empreendimento do mapa principal Mapbox (emp-* /
 * MapboxMap/EmpreendimentoModal.css), com todos os campos que o modal antigo
 * (MapaRevendaLocacao/ModalEmpreendimentos) exibia.
 */
class ImovelModal extends Component {
    /**
     * O crawler devolve `fotos` como [{url}], mas versões antigas do payload
     * usam string pura. Normaliza para o formato aceito pelo ImageSlider.
     */
    normalizeFotos = (fotos) => {
        if (!Array.isArray(fotos)) return [];
        return fotos
            .map((foto) => {
                if (!foto) return null;
                if (typeof foto === 'string') return { url: foto };
                const url = foto.url || foto.URL || foto.src;
                return url ? { url } : null;
            })
            .filter(Boolean);
    };

    money = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n) || n === 0) return '—';
        return currency.format(n);
    };

    metros = (value) => {
        const n = Number(value);
        if (!Number.isFinite(n) || n === 0) return '—';
        return `${number.format(n)} m²`;
    };

    precoMetro = (preco, area) => {
        const p = Number(preco);
        const a = Number(area);
        if (!p || !a) return '—';
        return currency.format(p / a);
    };

    enderecoCompleto = (e) => {
        const rua = [e.logradouro, e.numero].filter(Boolean).join(', ');
        const local = [e.bairro, e.cidade].filter(Boolean).join(', ');
        const uf = e.estado ? ` - ${e.estado}` : '';
        return [rua, local].filter(Boolean).join(' — ') + uf || '—';
    };

    renderHeader() {
        const e = this.props.empreendimento;

        if (this.props.loading || !e) {
            return (
                <div className="emp-head">
                    <div className="emp-head__icon"><i className="far fa-building" /></div>
                    <div className="emp-head__info">
                        <div className="emp-head__title">Carregando imóvel…</div>
                    </div>
                </div>
            );
        }

        const tipoNegocio = TIPO_NEGOCIO_LABELS[e.tipo_negocio] || e.tipo_negocio;
        const isVenda = String(e.tipo_negocio || '').toLowerCase() === 'venda';

        return (
            <div className="emp-head">
                <div className="emp-head__icon"><i className="far fa-building" /></div>
                <div className="emp-head__info">
                    <div className="emp-head__title">{e.titulo || 'Imóvel sem título'}</div>
                    <div className="emp-head__subtitle">
                        {[e.tipo, e.bairro, e.cidade].filter(Boolean).join(' • ')}
                    </div>
                    <div className="emp-head__meta">
                        {tipoNegocio && (
                            <span className={'emp-chip ' + (isVenda ? 'emp-chip--ok' : 'emp-chip--off')}>
                                <i className={isVenda ? 'fas fa-tag' : 'fas fa-key'} />
                                {tipoNegocio}
                            </span>
                        )}
                        {Number(e.preco) > 0 && (
                            <span className="emp-chip emp-chip--retrofit">
                                <i className="fas fa-coins" /> {currency.format(Number(e.preco))}
                            </span>
                        )}
                        {e.updatedAt && (
                            <span className="emp-chip emp-chip--retrofit">
                                <i className="far fa-clock" /> Atualizado em {moment(e.updatedAt).format('DD/MM/YYYY HH:mm')}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    renderSkeleton() {
        return (
            <div className="mapbox-emp-modal">
                <Skeleton height="240px" className="mb-3" />
                <Skeleton width="60%" className="mb-2" />
                <Skeleton width="40%" className="mb-2" />
                <Skeleton width="80%" />
            </div>
        );
    }

    renderBody() {
        const e = this.props.empreendimento;
        const fotos = this.normalizeFotos(e.fotos);
        const hasFotos = fotos.length > 0;
        const iptu = e.iptu ?? e.itpu;
        const vagas = e.vagas ?? e.parkingSpaces;

        return (
            <div className="mapbox-emp-modal">
                <TabView scrollable>
                    <TabPanel key="imovel" header="Imóvel">
                        <div className={hasFotos ? 'emp-two-col' : ''}>
                            {hasFotos && (
                                <div className="emp-photos">
                                    <ImageSlider key={`fotos-${e._id?.$oid || e._id || e.id}`} images={fotos} />
                                </div>
                            )}
                            <div className="emp-info">
                                <Section title="Localização" icon="fas fa-location-dot">
                                    <Item label="Endereço" value={this.enderecoCompleto(e)} full />
                                    <Item label="Bairro" value={e.bairro} />
                                    <Item label="Cidade" value={e.cidade} />
                                    <Item label="Estado" value={e.estado} />
                                    <Item label="CEP" value={e.cep} />
                                </Section>

                                <Section title="Características" icon="fas fa-ruler-combined">
                                    <Item label="Tipo do Imóvel" value={e.tipo} />
                                    <Item label="Tipo de Negócio" value={TIPO_NEGOCIO_LABELS[e.tipo_negocio] || e.tipo_negocio} />
                                    <Item label="Quartos" value={e.quartos ?? 0} />
                                    <Item label="Suítes" value={e.suites ?? 0} />
                                    <Item label="Vagas" value={vagas ?? 0} />
                                    <Item label="Área Total" value={this.metros(e.area_total)} />
                                    <Item label="Área Útil" value={this.metros(e.area_util)} />
                                    {e.area !== undefined && e.area !== null && e.area !== '' && (
                                        <Item label="Área (anúncio)" value={this.metros(e.area)} />
                                    )}
                                </Section>

                                <Section title="Valores" icon="fas fa-coins">
                                    <Item label="Preço" value={this.money(e.preco)} />
                                    <Item label="Preço m² Total" value={this.precoMetro(e.preco, e.area_total)} />
                                    <Item label="Preço m² Útil" value={this.precoMetro(e.preco, e.area_util)} />
                                    {e.preco_metro !== undefined && e.preco_metro !== null && e.preco_metro !== '' && (
                                        <Item label="Preço m² (anúncio)" value={this.money(e.preco_metro)} />
                                    )}
                                    <Item label="Condomínio" value={this.money(e.condominio)} />
                                    <Item label="IPTU" value={this.money(iptu)} />
                                </Section>
                            </div>
                        </div>
                    </TabPanel>

                    <TabPanel key="ficha" header="Ficha">
                        <Section title="Ficha do Anúncio" icon="fas fa-clipboard-list">
                            <Item label="Anunciante" value={e.anunciante} />
                            <Item label="Portal / Site" value={e.site} />
                            <Item label="Quartos" value={e.quartos ?? 0} />
                            <Item label="Suítes" value={e.suites ?? 0} />
                            <Item label="Vagas" value={vagas ?? 0} />
                            <Item label="IPTU" value={this.money(iptu)} />
                            <Item label="Condomínio" value={this.money(e.condominio)} />
                            <Item
                                label="Atualizado em"
                                value={e.updatedAt ? moment(e.updatedAt).format('DD/MM/YYYY HH:mm') : null}
                            />
                            <Item
                                label="Endereço do Anúncio"
                                full
                                value={
                                    e.anuncioUrl ? (
                                        <a
                                            className="emp-link-report"
                                            href={e.anuncioUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <i className="fas fa-up-right-from-square" /> {e.anuncioUrl}
                                        </a>
                                    ) : null
                                }
                            />
                        </Section>

                        {e.descricao && (
                            <Section title="Descrição" icon="fas fa-align-left" grid={false}>
                                <div
                                    className="emp-descricao"
                                    dangerouslySetInnerHTML={{ __html: e.descricao }}
                                />
                            </Section>
                        )}
                    </TabPanel>
                </TabView>
            </div>
        );
    }

    renderFooter() {
        const e = this.props.empreendimento;

        return (
            <div className="emp-footer">
                {e && e.anuncioUrl ? (
                    <a className="emp-link-report" href={e.anuncioUrl} target="_blank" rel="noopener noreferrer">
                        <i className="fas fa-up-right-from-square" /> Abrir anúncio original
                    </a>
                ) : <span />}
                <div className="emp-footer__actions">
                    <button className="emp-btn emp-btn--close" onClick={this.props.onHide}>
                        Fechar
                    </button>
                </div>
            </div>
        );
    }

    render() {
        const loading = this.props.loading || !this.props.empreendimento;

        return (
            <Dialog
                visible={this.props.show}
                onHide={this.props.onHide}
                header={this.renderHeader()}
                footer={this.renderFooter()}
                className="mapbox-emp-dialog"
                style={{ width: '70vw', zIndex: 9999999 }}
                breakpoints={{ '1200px': '85vw', '768px': '95vw' }}
                contentStyle={{ maxHeight: '78vh' }}
                dismissableMask
            >
                {loading ? this.renderSkeleton() : this.renderBody()}
            </Dialog>
        );
    }
}

export default ImovelModal;
